import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { groupBy } from '@progress/kendo-data-query';
import dayjs from 'dayjs';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { MarginDifference } from 'src/app/shared/lib/contract-request/models/contract-request-margin-differences.model';
import { interpolateParams } from 'src/app/shared/utilities/interpolate-params';
import { combine } from 'src/app/shared/utilities/route';
import { HOUSEWARE_OTHER_ID } from '../models';
import { ContractMinDaysStatus } from '../models/contract-min-days-status.model';
import { MarginCalculatorModel } from '../models/margin-calculator.model';
import { MarginContract } from '../models/margin-contract.model';
import { MarginsDifferences } from '../models/margins-differences.model';
import { EducationReimbursement, StateLicensureReimbursement } from '../models/reimbursement.model';
import { MarginCalculatorBaseService } from './margin-calculator-base.service';
import { FORM_NAMES, MarginCalculatorFormService } from './margin-calculator-form.service';
import { MoneyMovementValidation } from '../models/money-movement-validation.model';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { MarginType } from '../enum/margin-type.enum';

/** Id of the "Other" certificate */
const CertOtherId = 999999;
@Injectable({
    providedIn: 'root'
})
export class MarginCalculatorMarginService extends MarginCalculatorBaseService {
    private readonly _marginReceived = new Subject<MarginCalculatorModel>();
    private readonly _marginVersion = new Subject<number>();
    private readonly _reviewAndSendEnabled = new Subject<boolean>();
    private readonly _isContinueWithoutLinkingClicked = new Subject<boolean>();
    private readonly _minimumWageInViolation = new Subject<boolean>();
    private readonly _wfdSimplifiedSampleMarginFlag = this._featureManager.isEnabledSignal(
        FeatureFlag.WFDSimplifiedSampleMargin
    );
    public isContinueWithoutLinkingClicked$ = this._isContinueWithoutLinkingClicked.asObservable();

    readonly isMcPristineFacilityEnabled = this._featureManager.isEnabledSignal(
        FeatureFlag.IsMcPristineFacilityEnabled
    );

    constructor(
        private readonly _http: HttpClient,
        private readonly _marginFormService: MarginCalculatorFormService,
        private readonly _featureManager: LDFeatureManager
    ) {
        super();
    }

    get marginVersion$(): Observable<number> {
        return this._marginVersion.asObservable();
    }

    set marginVersion(version: number) {
        this._marginVersion.next(version);
    }

    get marginReviewAndSendEnabled$(): Observable<boolean> {
        return this._reviewAndSendEnabled.asObservable();
    }

    set marginReviewAndSendEnabled(isEnabled: boolean) {
        this._reviewAndSendEnabled.next(isEnabled);
    }

    get minimumWageInViolation$(): Observable<boolean> {
        return this._minimumWageInViolation.asObservable();
    }

    set minimumWageInViolation(value: boolean) {
        this._minimumWageInViolation.next(value);
    }

    get marginReceived$(): Observable<MarginCalculatorModel> {
        return this._marginReceived.asObservable();
    }

    set marginReceived(margin: MarginCalculatorModel) {
        this._marginReceived.next(margin);
    }

    postMargin(data: MarginCalculatorModel): Observable<number> {
        return this._http.post<number>(`${this.coreApiPath}/margin-calculator/margin`, data);
    }

    getMarginVersion(): Observable<number> {
        return this._http.get<number>(`${this.coreApiPath}/margin-calculator/margin-version`);
    }

    getMargin(mcId: number): Observable<MarginCalculatorModel> {
        const url = combine(this.coreApiPath, 'margin-calculator/margin');
        const params = interpolateParams({ mcId });
        return this._http.get<MarginCalculatorModel>(url, { params }).pipe(
            map((response) => {
                response.expenseAll.educationReimbursements.forEach((value: EducationReimbursement, index: number) => {
                    value.id = index + 1;
                });

                response.expenseAll.stateLicensures.forEach((value: StateLicensureReimbursement, index: number) => {
                    value.id = index + 1;
                });

                return response;
            })
        );
    }

    pipelineMargin(mcId: number): Observable<MarginContract> {
        return this._http.post<MarginContract>(`${this.coreApiPath}/margin-calculator/pipeline?mcid=${mcId}`, null);
    }

    getTargetMargin(facilityId: number, jobId?: number, isExtension = false): Observable<number> {
        const url = combine(this.coreApiPath, 'margin-calculator/target-margin');
        const params = interpolateParams({ facilityId, jobId, isExtension });
        return this._http.get<number>(url, { params });
    }

    getContractMarginBillRate(contractId: number): Observable<number> {
        const url = `${this.coreApiPath}/margin-calculator/ContractMarginBillRate/${contractId}`;
        return this._http.get<number>(url);
    }

    getContractModEmail(mcId: number): Observable<string[]> {
        const url = `${this.coreApiPath}/margin-calculator/${mcId}/contract-mod-email-addresses`;
        return this._http.get<string[]>(url);
    }

    getTargetMarginWithProfession(facilityId: number, professionId: number, isExtension = false): Observable<number> {
        return this._http.get<number>(
            `${this.coreApiPath}/margin-calculator/target-margin?facilityId=${facilityId}&professionId=${professionId}&isExtension=${isExtension}`
        );
    }

    getTargetMarginWithExpertise(
        facilityId: number,
        professionId: number,
        expertiseId: number,
        isExtension = false
    ): Observable<number> {
        return this._http.get<number>(
            `${this.coreApiPath}/margin-calculator/target-margin?facilityId=${facilityId}&professionId=${professionId}&expertiseId=${expertiseId}&isExtension=${isExtension}`
        );
    }

    getContractMinDaysStatus(
        candidateId: number,
        facilityId: number,
        billingRate: number,
        startDate: Date,
        endDate: Date
    ): Observable<ContractMinDaysStatus> {
        const url = combine(this.coreApiPath, 'margin-calculator/contract-min-days-status');
        const params = interpolateParams({ candidateId, facilityId, billingRate, startDate, endDate });
        return this._http.get<ContractMinDaysStatus>(url, { params });
    }

    getContractRequestSpecialRequestByMarginId(marginId: number): Observable<any> {
        return this._http.get<string>(`${this.coreApiPath}/contract-requests?marginId=${marginId}`);
    }

    getContractRequestPreviousMarginId(marginId: number): Observable<number> {
        return this._http.get<number>(`${this.coreApiPath}/contract-requests/getPreviousMargin?marginId=${marginId}`);
    }

    getContractModEmailChangeLogsSection(marginDifferences: MarginDifference[]): string {
        if (marginDifferences.length > 0) {
            const totalChargeBillName = 'Total Charge Bill';
            const totalChargePayName = 'Total Charge Pay';
            const chargePayName = 'Charge Pay';
            const chargeBillName = 'Charge Bill';

            const newMarginDifferences = marginDifferences.filter((x) => x.shouldSendEmail === true);
            const totalChargeBill = newMarginDifferences.find((x) => x.fieldName === totalChargeBillName);
            const totalChargePay = newMarginDifferences.find((x) => x.fieldName === totalChargePayName);
            const differencesMinusTotals = newMarginDifferences.filter(
                (x) => x.fieldName !== totalChargeBillName && x.fieldName !== totalChargePayName
            );

            const groups = groupBy(differencesMinusTotals, [{ field: 'effectiveDate' }]);
            let changeLogs = `\n\nChanges\n----------------`;

            groups.forEach((group) => {
                let effectiveDate = '';
                if (group.value === null) {
                    effectiveDate = `\n\nEffective Date: N/A`;
                } else {
                    const date = dayjs(group.value).format('MM/DD/YYYY');
                    effectiveDate = `\n\nEffective Date: ${date}`;
                }

                let changes = '';
                group.items.forEach((item) => {
                    const previousValue =
                        item.previousMarginValue === null || item.previousMarginValue === ''
                            ? ''
                            : `(from ${item.previousMarginValue})`;
                    if (item.fieldName !== chargePayName && item.fieldName !== chargeBillName) {
                        changes += `\n\t${item.fieldName}: ${item.currentMarginValue} ${previousValue}`;
                    }

                    //Anything with total charges for bill needs to be a subset of Charge Bill or a Base Bill Rate
                    if (totalChargeBill) {
                        const previousTotalValue =
                            totalChargeBill.previousMarginValue === null || totalChargeBill.previousMarginValue === ''
                                ? ''
                                : `(from ${totalChargeBill.previousMarginValue})`;
                        const totalValue = `\n\t${totalChargeBill.fieldName}: ${totalChargeBill.currentMarginValue} ${previousTotalValue}`;
                        if (item?.fieldName === chargeBillName) {
                            changes += totalValue;
                            //If there is a bump AND a Base bill rate, use ONLY the Charge Bill above as a single usage of date of that
                        } else if (
                            item?.fieldName === 'Base Bill Rate' &&
                            differencesMinusTotals.filter(
                                (x) => x.fieldName === chargeBillName || x.fieldName === 'Base Bill Rate'
                            ).length < 2
                        ) {
                            changes += totalValue;
                        }
                    }

                    //Anything with total charges for pay needs to be a subset of Charge Pay or a Base Pay Rate
                    if (totalChargePay) {
                        const previousTotalValue =
                            totalChargePay.previousMarginValue === null || totalChargePay.previousMarginValue === ''
                                ? ''
                                : `(from ${totalChargePay.previousMarginValue})`;
                        const totalValue = `\n\t${totalChargePay.fieldName}: ${totalChargePay.currentMarginValue} ${previousTotalValue}`;
                        if (item?.fieldName === chargePayName) {
                            changes += totalValue;
                            //If there is a bump AND a Base bill rate, use ONLY the Charge Pay above as a single usage of date of that
                        } else if (
                            item?.fieldName === 'Base Pay Rate' &&
                            differencesMinusTotals.filter(
                                (x) => x.fieldName === chargePayName || x.fieldName === 'Base Pay Rate'
                            ).length < 2
                        ) {
                            changes += totalValue;
                        }
                    }
                });

                changeLogs += `${effectiveDate}${changes}`;
            });

            return changeLogs;
        }
    }

    getMarginsDifferences(previousMarginId: number, currentMarginId: number): Observable<MarginsDifferences[]> {
        const url = `${this.coreApiPath}/margin-calculator/margins-differences?previousMarginId=${previousMarginId}&currentMarginId=${currentMarginId}`;
        return this._http.get<MarginsDifferences[]>(url);
    }

    duplicateSampleMargin(sampleMarginId: number, candidateId: number, offerId: number): Observable<number> {
        return this._http.post<number>(
            `${this.coreApiPath}/margin-calculator/duplicate-sample-margin/${sampleMarginId}/candidate/${candidateId}/offer/${offerId}`,
            null
        );
    }

    setContinueWithoutLinkingClicked(value: boolean): void {
        this._isContinueWithoutLinkingClicked.next(value);
    }

    /**
     * Validates whether margin can be saved.
     *
     * @param margin margin model to be validated
     * @returns Error message if model is invalid. Empty string if it's valid.
     */
    validateMarginBeforeSave(margin: MarginCalculatorModel): string {
        if (this.isMcPristineFacilityEnabled()) {
            if (!margin.globalFacilityKey) {
                return 'Save failed, facility global key not set. Please try again.';
            }
        }
        if (!margin.detail?.hospitalId) {
            return 'Save failed, facility not set. Please try again.';
        }

        if (margin.expenseAll.isVacation && margin.expenseAll.vacationDates === '') {
            return 'Vacation dates must be selected if vacation is enabled.';
        }

        if (!this._wfdSimplifiedSampleMarginFlag() || margin.marginTypeId !== MarginType.WorkforceDisruptionSample) {
            if (!this._marginFormService.validateForm(FORM_NAMES.reimbursement_details)) {
                if (
                    margin.expenseAll.oneTimeCustomReimbursementAmount > 0 &&
                    margin.expenseAll.oneTimeCustomReimbursementTitle === ''
                ) {
                    return 'Custom reimbursements must have a title.';
                }

                if (
                    margin.expenseAll.monthlyCustomReimbursementAmount > 0 &&
                    margin.expenseAll.monthlyCustomReimbursementTitle === ''
                ) {
                    return 'Custom reimbursements must have a title.';
                }
            }

            const formControl = this._marginFormService.parentForm
                .get(FORM_NAMES.reimbursement_details)
                ?.get(FORM_NAMES.pay_to);

            if (formControl) {
                formControl.markAllAsTouched();
            }
        }
        return null;
    }

    validateReferralBonus(referralBonus: number, payToReferral: string): string {
        if (referralBonus > 0 && !payToReferral) {
            return 'Please enter a value into the "Pay To" field.';
        }
        return null;
    }

    validateCertificates(selectedCerts: EducationReimbursement[], certificateOtherText: string): string {
        if (selectedCerts?.length) {
            const otherCertSelected = selectedCerts.find((item) => item.id === CertOtherId);
            if (otherCertSelected) {
                if (certificateOtherText === '') {
                    return 'Custom certification must have a title.';
                }
                if (certificateOtherText.toLowerCase() === 'other') {
                    return 'Custom certification must have a title other than "other".';
                }

                if (certificateOtherText.length < 3) {
                    return 'Custom certification must have a title over 2 characters.';
                }
            }
        }
    }

    validateHousingForm(margin: MarginCalculatorModel): string {
        if (!this._marginFormService.validateForm(FORM_NAMES.housing_details)) {
            if (margin.expenseAll.housewares?.length) {
                const custom = margin.expenseAll.housewares.find((ware) => ware.id === HOUSEWARE_OTHER_ID);
                if (custom && custom.avgCost > 0 && custom.title === '') {
                    return 'Custom houseware must have a title.';
                }
            }
        }
        return null;
    }

    getMoneyMovementValidations(marginId: number, userId: number): Observable<MoneyMovementValidation[]> {
        return this._http.get<MoneyMovementValidation[]>(
            `${this.coreApiPath}/margin-calculator/money-movement-validation/margin/${marginId}/user/${userId}`
        );
    }

    updateMarginWithDefaultEffectiveDates(margin: MarginCalculatorModel, isMedical: boolean, isDental: boolean): void {
        if (margin.insurance === null || margin.insurance === undefined) {
            margin.insurance = {
                medicalEffectiveDate: null,
                dentalEffectiveDate: null,
                savedAfterMedicalDentalEffectiveInsurance: false
            };
        }

        margin.insurance.medicalEffectiveDate =
            isMedical && margin.insurance != null ? margin.insurance?.medicalEffectiveDate : null;
        margin.insurance.dentalEffectiveDate =
            isDental && margin.insurance != null ? margin.insurance.dentalEffectiveDate : null;
    }
}
